import { PlayIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ToolInterface } from "../../../api/content/authoring/processContentDecoders";
import { newClientKey, requestTurn } from "../../../api/threads/threadsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import Field from "../../../ui/forms/Field";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import ParameterControl from "../../processAuthoring/steps/ParameterControl";
import { useToolInterface } from "../../processAuthoring/steps/useToolInterface";
import { useThreadContext } from "../page/threadPageContext";
import type { PickedTool } from "./mentions";
import { toolInputValues } from "./toolRunValues";

interface ToolRunFormProps {
  tool: PickedTool;
  /** Whether the Thread can take a run now: it is live and no turn is open. */
  canRun: boolean;
  onRequested: () => void;
}

/**
 * Run a tool given in Add… yourself (W2-6, F21): its parameters and inputs, then the run as
 * the Thread's own turn on the host's connector. Its outputs appear in the Thread.
 */
export default function ToolRunForm({ tool, canRun, onRequested }: ToolRunFormProps) {
  const { t } = useTranslation();
  const declared = useToolInterface(tool.reference, null);
  return (
    <div className="space-y-2 border-l-2 pl-3">
      <LoadStatus state={declared} loadingLabel={t("threads.composer.toolRun.loading")} compact={true} />
      {declared.status === "loaded" && declared.value !== null ? <DeclaredRun tool={tool} declared={declared.value} canRun={canRun} onRequested={onRequested} /> : null}
    </div>
  );
}

function DeclaredRun({ tool, declared, canRun, onRequested }: ToolRunFormProps & { declared: ToolInterface }) {
  const { t } = useTranslation();
  const { threadId } = useThreadContext();
  const [parameters, setParameters] = useState<Record<string, unknown>>({});
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [clientRequestId] = useState(newClientKey);
  const { state, submit } = useSubmission();
  const values = toolInputValues(declared.inputs, inputs);

  const run = () => {
    if (values.kind !== "ready") return;
    submit(async () => {
      await requestTurn(threadId, {
        kind: "step",
        stepKey: null,
        clientRequestId,
        inputValues: values.values,
        tool: { reference: tool.reference, parameters: Object.fromEntries(Object.entries(parameters).filter(([, value]) => value !== undefined)) },
      });
      onRequested();
    });
  };

  return (
    <>
      <p className="app-text-muted text-xs">{declared.description}</p>
      {Object.entries(declared.inputs).map(([name, input]) => (
        <Field
          key={name}
          id={`tool-run-${name}`}
          label={
            <>
              <span className="font-mono">{name}</span> <span className="app-text-muted text-xs">{input.accepts.join(", ")}</span>
            </>
          }
          hint={input.description ?? undefined}
          errors={values.kind === "invalid" && values.input === name ? <span className="app-error-text text-xs">{t("threads.composer.toolRun.notJson")}</span> : undefined}
        >
          {(control) => (
            <textarea
              {...control}
              rows={2}
              className={`${FORM_CONTROL_CLASS} font-mono text-xs`}
              required={input.required}
              value={inputs[name] ?? ""}
              onChange={(event) => { setInputs((current) => ({ ...current, [name]: event.target.value })); }}
            />
          )}
        </Field>
      ))}
      {Object.entries(declared.parameters).map(([name, parameter]) => (
        <ParameterControl key={name} name={name} parameter={parameter} value={parameters[name]} onChange={(value) => { setParameters((current) => ({ ...current, [name]: value })); }} />
      ))}
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <AppButton variant="primary" size="sm" onClick={run} disabled={!canRun || values.kind !== "ready" || state.status === "submitting"}>
        <PlayIcon className="h-4 w-4" aria-hidden="true" />
        {t("threads.composer.toolRun.run", { tool: tool.name })}
      </AppButton>
    </>
  );
}
